import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { resolvePageTitle } from '../config/pageTitles';

type PageTitleContextValue = {
  /** Effective title shown in the app navbar */
  title: string;
  /** Optional override from the active page (detail screens, etc.) */
  setTitleOverride: (title: string | null) => void;
};

const PageTitleContext = createContext<PageTitleContextValue | null>(null);

export const PageTitleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [override, setTitleOverride] = useState<string | null>(null);

  // Clear overrides when the route changes
  useEffect(() => {
    setTitleOverride(null);
  }, [location.pathname]);

  const routeTitle = useMemo(() => resolvePageTitle(location.pathname), [location.pathname]);
  const title = override?.trim() || routeTitle;

  useEffect(() => {
    document.title = title && title !== 'Gati Sutra' ? `${title} · Gati Sutra` : 'Gati Sutra';
  }, [title]);

  const value = useMemo(() => ({ title, setTitleOverride }), [title]);

  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
};

export function usePageTitleContext(): PageTitleContextValue {
  const ctx = useContext(PageTitleContext);
  if (!ctx) {
    throw new Error('usePageTitleContext must be used within PageTitleProvider');
  }
  return ctx;
}

/**
 * Register a page title in the global navbar (and browser tab).
 * Prefer this for dynamic titles (e.g. docket number). Static pages can rely on route resolution.
 *
 * Safe to call at the top of any page under App's PageTitleProvider (outside DashboardLayout).
 */
export function usePageTitle(title: string | null | undefined): void {
  const { setTitleOverride } = usePageTitleContext();
  useEffect(() => {
    if (title == null || title === '') {
      setTitleOverride(null);
      return;
    }
    setTitleOverride(title);
    return () => setTitleOverride(null);
  }, [title, setTitleOverride]);
}
